import React, { useState } from "react";
import { AiOutlineArrowLeft, AiOutlineSend } from "react-icons/ai";
import { useAuth } from "../../../../Contexts/AuthContext/AuthContext";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { baseUrl } from "../../../../Environments/environment";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api";

// Function to fetch broadcast data
const fetchBroadcast = async (
	baseUrl,
	propertyId,
	broadcastId,
	accessToken
) => {
	const response = await apiClient.get(
		`${baseUrl}/broadcast/${propertyId}/${broadcastId}`,
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
			withCredentials: true,
		}
	);
	if (response.ok) {
		const newToken = response.headers.get("Authorization");
		if (newToken) {
			localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
		}
	}
	return response.data.result.broadcast;
};

export const BroadcastScreen = ({
	guestIds = [],
	broadcast = null,
	onClose,
	createClose,
}) => {
	const { accessToken } = useAuth();
	const { selectedPropertyId } = useProperty();
	const [broadcastId, setBroadcastId] = useState(broadcast?._id);
	const queryClient = useQueryClient();

	const [messageBody, setMessageBody] = useState("");

	const {
		data: broadcastData = {},
		isLoading,
		error,
	} = useQuery({
		queryKey: ["broadcast", selectedPropertyId, broadcastId],
		queryFn: () =>
			fetchBroadcast(baseUrl, selectedPropertyId, broadcastId, accessToken),
		staleTime: 60000,
		cacheTime: 600000,
		enabled: !!broadcastId, // Only fetch if broadcastId is truthy
	});

	const sendMessage = async () => {
		if (!broadcastId && !guestIds.length) {
			toast.error("Broadcast ID or guest IDs are required.");
			return;
		}

		try {
			const payload = {
				body: messageBody,
				broadcastId: broadcastId || undefined,
				guestIds: guestIds.length ? guestIds : undefined,
			};

			const response = await apiClient.post(
				`${baseUrl}/broadcast/${selectedPropertyId}/createupdatesend`,
				payload
			);

			if (response.ok) {
				const newToken = response.headers.get("Authorization");
				if (newToken) {
					localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
				}
			}

			setMessageBody(""); // Clear the input field after sending
			const newBroadcastId = response.data.result.broadcast._id;

			// Set the new broadcastId from the response
			setBroadcastId(newBroadcastId);

			// Invalidate the broadcast query with the updated broadcastId

			queryClient.invalidateQueries({
				queryKey: ["broadcast", selectedPropertyId, newBroadcastId],
			});
			createClose();
		} catch (error) {
			const errorMessage = error.response?.data?.message;
			toast.error(errorMessage);
		}
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-end z-50">
			<div className="bg-white p-0 rounded-xl rounded-t-2xl shadow-lg max-w-md w-[30vw] min-w-[350px] m-4 relative">
				<div className="bg-gray-100 p-4 rounded-t-xl border-b border-gray-200">
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-2">
							<button
								onClick={onClose}
								className="text-indigo-700 border bg-white h-10 w-10 flex items-center justify-center rounded-xl focus:outline-none"
							>
								<AiOutlineArrowLeft className="h-5 w-5" />
							</button>

							<div>
								<div className="flex items-center space-x-2">
									<h2 className="text-gray-800 font-bold">
										{broadcast
											? `${broadcast.guestIds?.length || 0} Guests`
											: `${guestIds.length} Guests`}
									</h2>
								</div>
								<div className="flex space-x-4 text-xs text-gray-500 mt-2">
									{/* Add any other relevant details here if needed */}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="p-4 h-[60vh] overflow-y-scroll">
					{/* Display broadcast messages if needed */}
					{isLoading ? (
						<p>Loading</p>
					) : error ? (
						<div className="text-red-500">Failed to load messages</div>
					) : (
						<ul className="space-y-1">
							{broadcastData.broadcastMessages?.map((message) => (
								<li
									key={message._id}
									className="text-right" // Broadcast messages are likely from the host, so left-aligned
								>
									<div
										className={`inline-block px-4 p-2 text-sm max-w-[300px] bg-indigo-600 text-gray-100 rounded-bl-3xl rounded-t-3xl text-start`}
									>
										{message.content}
									</div>
									<div className="text-gray-500 mt-1 text-xs text-[0.75em]">
										{new Date(message.createdAt).toLocaleTimeString([], {
											hour: "2-digit",
											minute: "2-digit",
										})}
									</div>
								</li>
							))}
						</ul>
					)}
				</div>

				<hr className="border-gray-200" />

				{/* Send message in broadcast */}
				<div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 rounded-b-xl">
					<input
						type="text"
						className="flex-grow px-4 py-2 text-sm border-0 border-gray-300 rounded-xl"
						placeholder="Type your message..."
						value={messageBody}
						onChange={(e) => setMessageBody(e.target.value)}
					/>

					<button
						type="submit"
						className="ml-3 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
						onClick={sendMessage}
					>
						<AiOutlineSend size={20} />
					</button>
				</div>
			</div>
		</div>
	);
};
